import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Gavel, ArrowUpDown, RefreshCw, FileEdit } from 'lucide-react';

import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { getLeads } from '../services/operatoreApi';

const STATI = [
  'nuovo', 'in verifica', 'confermato', 'contattato', 'iscritto', 'vertenza aperta', 'chiuso senza esito',
];

const SETTORI = ['logistica', 'trasporto', 'corrieri', 'commercio', 'metalmeccanica', 'chimico', 'altro'];

const STATO_STILE = {
  nuovo: 'bg-blue-50 text-blue-700 border-blue-200',
  'in verifica': 'bg-amber-50 text-amber-700 border-amber-200',
  confermato: 'bg-purple-50 text-purple-700 border-purple-200',
  contattato: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  iscritto: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'vertenza aperta': 'bg-red-50 text-red-700 border-red-200',
  'chiuso senza esito': 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function OperatorePage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [filtroSettore, setFiltroSettore] = useState('tutti');
  const [ordinaPer, setOrdinaPer] = useState('priorita');

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore('');
    try {
      const risposta = await getLeads({
        stato: filtroStato !== 'tutti' ? filtroStato : undefined,
        settore: filtroSettore !== 'tutti' ? filtroSettore : undefined,
        ordinaPer,
      });
      setLeads(risposta.leads);
    } catch (err) {
      if (err.response?.status === 403) {
        setErrore('Accesso riservato ai consulenti.');
      } else {
        setErrore('Errore nel caricamento della coda lead.');
      }
    } finally {
      setLoading(false);
    }
  }, [filtroStato, filtroSettore, ordinaPer]);

  useEffect(() => { carica(); }, [carica]);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="operatore-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
              <Gavel className="w-6 h-6 text-forest" /> Area Consulenza
            </h1>
            <p className="text-muted-foreground mt-1">Coda dei lavoratori che hanno richiesto un contatto</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" asChild data-testid="editor-ccnl-link">
              <Link to="/operatore/ccnl">
                <FileEdit className="w-4 h-4 mr-2" /> Editor CCNL
              </Link>
            </Button>
            <Button variant="outline" onClick={carica} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
            </Button>
          </div>
        </div>

        {errore && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">{errore}</div>
        )}

        <Card className="swiss-card">
          <CardHeader>
            <CardTitle className="text-lg">Filtri</CardTitle>
            <CardDescription>Filtra per stato o settore, ordina per priorità o data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Select value={filtroStato} onValueChange={setFiltroStato}>
                <SelectTrigger className="swiss-input" data-testid="filtro-stato">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti gli stati</SelectItem>
                  {STATI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={filtroSettore} onValueChange={setFiltroSettore}>
                <SelectTrigger className="swiss-input" data-testid="filtro-settore">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti i settori</SelectItem>
                  {SETTORI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                onClick={() => setOrdinaPer((p) => (p === 'priorita' ? 'data' : 'priorita'))}
                data-testid="toggle-ordinamento"
              >
                <ArrowUpDown className="w-4 h-4 mr-2" />
                Ordina per {ordinaPer === 'priorita' ? 'priorità' : 'data'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="swiss-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Caricamento...</div>
            ) : leads.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">Nessun caso trovato con questi filtri.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Priorità</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Settore</TableHead>
                    <TableHead>Contratto</TableHead>
                    <TableHead>Creato il</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow
                      key={lead.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/operatore/leads/${lead.id}`)}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <TableCell className="font-mono font-bold">{lead.punteggio_priorita}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={STATO_STILE[lead.stato] || ''}>{lead.stato}</Badge>
                      </TableCell>
                      <TableCell className="capitalize">{lead.settore_dichiarato}</TableCell>
                      <TableCell>{lead.tipo_contratto}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(lead.creato_il).toLocaleString('it-IT')}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">Apri</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
